import React, { useState, useEffect } from 'react';
import { getContextualError } from '../utils/errorMessages';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { lifeAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, DollarSign, Calendar, Bell, CreditCard, Trash2, User, Receipt, LogOut, Crown, Sparkles, LayoutDashboard } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useToast } from '../hooks/use-toast';
import EditProfileModal from '../components/EditProfileModal';
import PaymentHistoryModal from '../components/PaymentHistoryModal';
import LifeCalendar from '../components/LifeCalendar';
import SmartFinancialDashboard from '../components/SmartFinancialDashboard';
// 🧠 Emotional Mode Awareness
import useEmotionalMode from '../hooks/useEmotionalMode';

const LifeMode = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout, refreshUser } = useAuth();
  const { toast } = useToast();
  const { t } = useLocale();
  // 🧠 Emotional Mode Hook
  const { emotionalMode, ambientEmotional, shouldShowUrgentLanguage } = useEmotionalMode();
  const [subscription, setSubscription] = useState(null);
  const [bills, setBills] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [monthlyExpenses, setMonthlyExpenses] = useState(0);
  const [expenses, setExpenses] = useState([]);
  const [calendarRefresh, setCalendarRefresh] = useState(0);
  const [billDialogOpen, setBillDialogOpen] = useState(false);
  const [apptDialogOpen, setApptDialogOpen] = useState(false);
  const [newBill, setNewBill] = useState({ name: '', amount: '', dueDate: '', category: 'utilities' });
  const [newAppointment, setNewAppointment] = useState({ type: '', date: '', time: '', location: '' });
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  
  // Get initial tab from URL parameter (?tab=bills or ?tab=calendar)
  const getInitialTab = () => {
    const tab = searchParams.get('tab');
    if (tab === 'bills' || tab === 'dashboard') return 'dashboard';
    if (tab === 'calendar') return 'calendar';
    return 'financial'; // default
  };
  const [activeTab, setActiveTab] = useState(getInitialTab());

  useEffect(() => {
    loadUserSubscription();
  }, []);

  const loadUserSubscription = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/subscription`, {
        credentials: 'include'
      });
      const data = await response.json();
      setSubscription(data);
    } catch (error) {
      console.error('Error loading subscription:', error);
    }
  };

  const loadData = async () => {
    try {
      const [billsResponse, appointmentsResponse, expensesResponse, expensesListResponse] = await Promise.all([
        lifeAPI.getBills(),
        lifeAPI.getAppointments(),
        lifeAPI.getMonthlyExpenses(),
        lifeAPI.getExpenses()
      ]);
      
      setBills(billsResponse.data || []);
      setAppointments(appointmentsResponse.data || []);
      setMonthlyExpenses(expensesResponse.data?.total || 0);
      setExpenses(expensesListResponse.data || []);
    } catch (error) {
      console.error('Error loading life data:', error);
      setBills([]);
      setAppointments([]);
      setMonthlyExpenses(0);
      setExpenses([]);
    }
  };

  useEffect(() => {
    const initData = async () => {
      await loadData();
    };
    initData();
  }, []);

  const handleAddBill = async () => {
    if (!newBill.name.trim() || !newBill.amount || !newBill.dueDate) {
      toast({ title: 'Error', description: 'Please fill in all bill fields', variant: 'destructive' });
      return;
    }
    
    try {
      console.log('💰 Adding bill:', newBill);
      await lifeAPI.createBill({
        name: newBill.name,
        amount: parseFloat(newBill.amount),
        dueDate: newBill.dueDate,
        category: newBill.category
      });
      console.log('✅ Bill added successfully');
      toast({ title: '💰 Bill Added!', description: `${newBill.name} - $${newBill.amount}` });
      setBillDialogOpen(false);
      setNewBill({ name: '', amount: '', dueDate: '', category: 'utilities' });
      await loadData();
      setCalendarRefresh(prev => prev + 1); // Trigger calendar refresh
    } catch (error) {
      console.error('❌ Error adding bill:', error);
      toast({ title: 'Error', description: getContextualError(error, 'life.addBill'), variant: 'destructive' });
      // Close dialog anyway after showing error
      setBillDialogOpen(false);
      setNewBill({ name: '', amount: '', dueDate: '', category: 'utilities' });
    }
  };

  const handlePayBill = async (bill) => {
    try {
      // Mark bill as paid (no longer auto-creates expense to avoid double counting)
      await lifeAPI.updateBill(bill.id, { paid: true });
      
      toast({ title: '✅ Bill Paid!', description: `${bill.name} marked as paid` });
      await loadData();
      setCalendarRefresh(prev => prev + 1); // Trigger calendar refresh
    } catch (error) {
      toast({ title: 'Unable to Update', description: getContextualError(error, 'life.updateBill'), variant: 'destructive' });
    }
  };

  const handleDeleteBill = async (bill) => {
    try {
      await lifeAPI.deleteBill(bill.id);
      toast({ title: '🗑️ Removed', description: `${bill.name} deleted` });
      await loadData();
      setCalendarRefresh(prev => prev + 1); // Trigger calendar refresh
    } catch (error) {
      toast({ title: 'Unable to Delete', description: getContextualError(error, 'life.updateBill'), variant: 'destructive' });
    }
  };

  const handleAddAppointment = async () => {
    if (!newAppointment.type.trim() || !newAppointment.date || !newAppointment.time) {
      toast({ title: 'Error', description: 'Please fill in appointment details', variant: 'destructive' });
      return;
    }
    
    try {
      console.log('📅 Adding appointment:', newAppointment);
      await lifeAPI.createAppointment({
        title: newAppointment.type,
        date: newAppointment.date,
        time: newAppointment.time,
        location: newAppointment.location
      });
      console.log('✅ Appointment added successfully');
      toast({ title: '📅 Appointment Added!', description: `${newAppointment.type} on ${newAppointment.date}` });
      setApptDialogOpen(false);
      setNewAppointment({ type: '', date: '', time: '', location: '' });
      await loadData();
      setCalendarRefresh(prev => prev + 1); // Trigger calendar refresh
    } catch (error) {
      console.error('❌ Error adding appointment:', error);
      toast({ title: 'Error', description: error.message || 'Failed to add appointment', variant: 'destructive' });
      // Close dialog anyway after showing error
      setApptDialogOpen(false);
      setNewAppointment({ type: '', date: '', time: '', location: '' });
    }
  };

  const handleDeleteAppointment = async (appt) => {
    try {
      await lifeAPI.deleteAppointment(appt.id);
      toast({ title: '🗑️ Removed', description: `${appt.title || appt.type} deleted` });
      await loadData();
      setCalendarRefresh(prev => prev + 1); // Trigger calendar refresh
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete appointment', variant: 'destructive' });
    }
  };

  const unpaidBills = bills.filter(b => !b.paid);

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50/80 via-cyan-50/60 to-white relative overflow-x-hidden overflow-y-auto">
      
      <header className="glass-strong border-b border-white/40 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/home')} className="hover:bg-teal-100/60 transition-all duration-200">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center text-white text-xl shadow-soft">
                  💰
                </div>
                <div>
                  <h1 className="text-xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">{t('life.title')}</h1>
                  <p className="text-xs text-gray-500 font-medium">{t('cards.lifeDesc')} ✨</p>
                </div>
              </div>
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
                  <User className="h-6 w-6 text-gray-700" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-80" align="end">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex items-center space-x-3 p-2">
                    <div className="h-9 w-9 rounded-full bg-teal-600 flex items-center justify-center text-white font-bold text-sm">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                {subscription && (
                  <>
                    <div className="px-3 py-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{t('life.subscription')}</span>
                        {subscription.subscription?.plan_id !== 'free' ? (
                          <div className="flex items-center gap-1 bg-teal-100 text-teal-800 px-2 py-1 rounded-full">
                            <Crown className="h-3 w-3" />
                            <span className="text-xs font-medium">{t('life.pro')}</span>
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-xs">{t('life.freePlan')}</Badge>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{t('life.aiAccess')}</span>
                        {subscription.subscription?.plan_id !== 'free' ? (
                          <div className="flex items-center gap-1">
                            <Sparkles className="h-3 w-3 text-teal-600" />
                            <span className="text-sm font-semibold text-teal-600">{t('life.unlimited')}</span>
                          </div>
                        ) : (
                          <span className="text-sm font-semibold text-gray-900">{subscription.credits || 0} {t('life.credits')}</span>
                        )}
                      </div>
                    </div>
                    <DropdownMenuSeparator />
                  </>
                )}
                
                <DropdownMenuItem onClick={() => setShowEditProfile(true)}>
                  <User className="mr-2 h-4 w-4" />
                  <span>{t('settings.editProfile')}</span>
                </DropdownMenuItem>
                
                <DropdownMenuItem onClick={() => navigate('/pricing')}>
                  <CreditCard className="mr-2 h-4 w-4" />
                  <span>{t('settings.manageSubscription')}</span>
                </DropdownMenuItem>
                
                <DropdownMenuItem onClick={() => setShowPaymentHistory(true)}>
                  <Receipt className="mr-2 h-4 w-4" />
                  <span>{t('settings.paymentHistory')}</span>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                
                <DropdownMenuItem onClick={logout} className="text-red-600">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>{t('settings.logOut')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Modals */}
            <EditProfileModal
              isOpen={showEditProfile}
              onClose={() => setShowEditProfile(false)}
              user={user}
              onUpdate={refreshUser}
            />
            <PaymentHistoryModal
              isOpen={showPaymentHistory}
              onClose={() => setShowPaymentHistory(false)}
            />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex w-full max-w-xl mx-auto mb-8 p-1 bg-gray-100/80 rounded-xl gap-1">
            <TabsTrigger 
              value="financial"
              className="flex-1 py-2.5 px-3 text-xs sm:text-sm rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all"
            >
              <Sparkles className="h-4 w-4 mr-1.5 flex-shrink-0" />
              <span className="truncate">{t('life.smartFinancial')}</span>
            </TabsTrigger>
            <TabsTrigger 
              value="dashboard"
              className="flex-1 py-2.5 px-3 text-xs sm:text-sm rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all"
            >
              <LayoutDashboard className="h-4 w-4 mr-1.5 flex-shrink-0" />
              <span className="truncate">{t('life.billsAndGoals')}</span>
            </TabsTrigger>
            <TabsTrigger 
              value="calendar"
              className="flex-1 py-2.5 px-3 text-xs sm:text-sm rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all"
            >
              <Calendar className="h-4 w-4 mr-1.5 flex-shrink-0" />
              <span className="truncate">{t('life.calendar')}</span>
            </TabsTrigger>
          </TabsList>

          {/* Smart Financial Dashboard Tab */}
          <TabsContent value="financial" className="space-y-8">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-violet-600 bg-clip-text text-transparent mb-2">
                {t('life.smartFinancialDashboardTitle')}
              </h2>
              <p className="text-gray-600">{t('life.aiPoweredInsights')}</p>
            </div>
            
            <SmartFinancialDashboard 
              bills={bills} 
              expenses={expenses} 
              onRefresh={loadData}
            />
          </TabsContent>

          <TabsContent value="dashboard" className="space-y-8">

            {/* Bills and Appointments Management */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <CardTitle className="flex items-center">
                  <Bell className="h-5 w-5 mr-2 text-orange-600" />
                  {t('life.bills')}
                </CardTitle>
                <Dialog open={billDialogOpen} onOpenChange={setBillDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-orange-600 hover:bg-orange-700">+ {t('life.addBill')}</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('life.addBill')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>{t('life.billName')}</Label>
                        <Input placeholder="e.g., Electricity" value={newBill.name} onChange={(e) => setNewBill({ ...newBill, name: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('life.amount')} ($)</Label>
                        <Input type="number" placeholder="0.00" value={newBill.amount} onChange={(e) => setNewBill({ ...newBill, amount: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('life.dueDate')}</Label>
                        <Input type="date" value={newBill.dueDate} onChange={(e) => setNewBill({ ...newBill, dueDate: e.target.value })} />
                      </div>
                      <Button onClick={handleAddBill} className="w-full bg-orange-600 hover:bg-orange-700">{t('life.addBill')}</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent className="space-y-3">
                {unpaidBills.length === 0 ? (
                  <div className="text-center py-12 bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg border-2 border-dashed border-orange-200">
                    <Bell className="h-16 w-16 mx-auto mb-4 text-orange-300" />
                    <p className="text-gray-700 font-medium">{t('life.allClear')}</p>
                    <p className="text-sm text-gray-500 mt-1">{t('life.helpRemember')}</p>
                  </div>
                ) : (
                  <>
                    {/* 🧠 Emotional Mode: Gentle reminder message */}
                    {(emotionalMode || ambientEmotional) && (
                      <div className="p-3 bg-teal-50 border border-teal-100 rounded-lg mb-2">
                        <p className="text-sm text-teal-600">💜 {t('life.theseAreHere') || "These are here when you're ready — no rush"}</p>
                      </div>
                    )}
                    {unpaidBills.map((bill) => (
                    <div key={bill.id} className={`p-4 border-2 rounded-lg hover:shadow-md transition-all ${
                      emotionalMode || ambientEmotional 
                        ? 'border-teal-100 bg-teal-50/30' 
                        : 'border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{bill.name}</h3>
                          {/* 🧠 Emotional Mode: Soften "Due" language */}
                          <p className="text-sm text-gray-600">
                            {emotionalMode || ambientEmotional 
                              ? `📅 ${t('life.scheduled') || 'Scheduled'}: ${bill.dueDate}` 
                              : `📅 ${t('life.due') || 'Due'}: ${bill.dueDate}`}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="text-right">
                            <p className="text-2xl font-bold text-gray-900">${bill.amount}</p>
                            <Button size="sm" className="mt-2 bg-green-600 hover:bg-green-700" onClick={() => handlePayBill(bill)}>✓ Mark Paid</Button>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteBill(bill)}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <div>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <CardTitle className="flex items-center">
                  <Calendar className="h-5 w-5 mr-2 text-teal-600" />
                  {t('life.appointments')}
                </CardTitle>
                <Dialog open={apptDialogOpen} onOpenChange={setApptDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-teal-600 hover:bg-teal-700">+ {t('common.add')}</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('life.addAppointmentTitle')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>{t('life.type')}</Label>
                        <Input placeholder="e.g., Doctor" value={newAppointment.type} onChange={(e) => setNewAppointment({ ...newAppointment, type: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('life.date')}</Label>
                        <Input type="date" value={newAppointment.date} onChange={(e) => setNewAppointment({ ...newAppointment, date: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('life.time')}</Label>
                        <Input type="time" value={newAppointment.time} onChange={(e) => setNewAppointment({ ...newAppointment, time: e.target.value })} />
                      </div>
                      <Button onClick={handleAddAppointment} className="w-full bg-teal-600 hover:bg-teal-700">{t('life.addAppointment')}</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent className="space-y-3">
                {appointments.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">{t('life.noAppointmentsShort')}</p>
                  </div>
                ) : (
                  appointments.map((appt) => (
                    <div key={appt.id} className="p-3 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-lg border border-teal-100">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900 text-sm">{appt.title || appt.type}</h3>
                          <p className="text-xs text-gray-600 mt-1">📅 {appt.date} at {appt.time}</p>
                          {appt.location && <p className="text-xs text-gray-500 mt-1">📍 {appt.location}</p>}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteAppointment(appt)}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </div>
          </TabsContent>

          <TabsContent value="calendar" className="space-y-6">
            <LifeCalendar key={calendarRefresh} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default LifeMode;
